import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/feature/SEOHead';
import SchemaOrg from '@/components/base/SchemaOrg';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { getArticleById, articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const article = getArticleById(id || '');
  const category = categories.find((c) => c.slug === article?.category);
  const relatedArticles = article
    ? articles.filter((a) => a.category === article.category && a.id !== article.id).slice(0, 3)
    : [];

  if (!article) {
    return (
      <main className="pt-16 flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-error-warning-line text-2xl"></i>
          </div>
          <h1 className="mt-4 text-xl font-heading font-semibold text-foreground-900">文章未找到</h1>
          <p className="mt-2 text-sm text-foreground-500">该文章可能已被删除或移至其他位置。</p>
          <Link
            to="/"
            className="inline-block mt-4 px-5 py-2 rounded-md bg-primary-500 text-background-50 dark:text-foreground-950 text-sm font-medium hover:bg-primary-600 transition-colors duration-200"
          >
            返回首页
          </Link>
        </div>
      </main>
    );
  }

  return (
    <>
      <SEOHead
        title={`${article.title}——高柳家`}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://takayanagi-family.com/article/${article.id}`}
        ogTitle={article.title}
        ogDescription={article.excerpt}
        ogType="article"
        ogImage={article.image}
      />
      <SchemaOrg type="Article" data={{
        headline: article.title,
        description: article.excerpt,
        image: article.image,
        datePublished: article.date,
        author: { '@type': 'Person', name: article.author },
        publisher: {
          '@type': 'Organization',
          name: '高柳家',
        },
        inLanguage: 'zh-Hans',
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': `https://takayanagi-family.com/article/${article.id}`,
        },
      }} />

      <main className="pt-16">
        {/* Article Header */}
        <section className="relative w-full h-[300px] md:h-[420px] flex items-end overflow-hidden">
          <img
            src={article.image}
            alt={article.title}
            title={`高柳家——${article.title}`}
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/30 to-transparent"></div>
          <div className="relative z-10 w-full max-w-4xl mx-auto px-4 md:px-6 pb-8 md:pb-12">
            {category && (
              <Link
                to={`/category/${category.slug}`}
                className="inline-block px-3 py-1 rounded-full bg-white/20 text-white text-xs font-medium mb-3 hover:bg-white/30 transition-colors duration-200"
              >
                {category.name}
              </Link>
            )}
            <h1 className="text-xl md:text-3xl lg:text-4xl font-heading font-semibold text-white leading-tight">
              {article.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 mt-3 text-white/70 text-xs md:text-sm">
              <span>{article.author}</span>
              <span>{article.date}</span>
              <span>{article.readTime}</span>
            </div>
          </div>
        </section>

        {/* Breadcrumb */}
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: category?.name || '分类', href: category ? `/category/${category.slug}` : undefined },
              { label: article.title },
            ]}
          />
        </div>

        {/* Article Content */}
        <article className="py-10 md:py-14 bg-background-50">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <div
              className="prose-custom text-foreground-800 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            {/* Tags */}
            <div className="mt-10 pt-6 border-t border-background-200">
              <div className="flex flex-wrap gap-2">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 rounded-full bg-background-100 text-xs text-foreground-600"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </article>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="py-10 md:py-14 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900">相关文章</h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 md:gap-6 mt-6">
                {relatedArticles.map((related) => (
                  <Link
                    key={related.id}
                    to={`/article/${related.id}`}
                    className="group block rounded-lg overflow-hidden bg-background-50 transition-all duration-300 hover:scale-[1.02]"
                  >
                    <div className="relative h-40 overflow-hidden">
                      <img
                        src={related.image}
                        alt={related.title}
                        title={related.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="text-sm font-heading font-semibold text-foreground-900 line-clamp-2 leading-snug group-hover:text-primary-600 transition-colors duration-200">
                        {related.title}
                      </h3>
                      <p className="mt-1 text-xs text-foreground-500">{related.date} · {related.readTime}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
    </>
  );
}